import React from 'react';
import { binImages } from '../data/mock';
import { Button } from './ui/button';
import { Shield, Clock, Leaf, Award, Recycle, Truck } from 'lucide-react';

const About = () => {
  const features = [
    { icon: Shield, title: "No Hidden Fees", description: "What we quote is what you pay" },
    { icon: Clock, title: "On-Time Delivery", description: "We respect your schedule" },
    { icon: Recycle, title: "Eco-Conscious", description: "We recycle what we can" },
    { icon: Truck, title: "Fast Pickup", description: "Ready when you are" }
  ];

  return (
    <section id="about" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Top Section - Different Layout */}
        <div className="text-center mb-16">
          <span className="text-blue-600 font-semibold tracking-wider uppercase text-sm">About Our Services</span>
          <h2 className="text-4xl sm:text-5xl font-bold text-[#1a365d] mt-4 mb-6">
            Waste Disposal, <span className="text-blue-500">Simplified</span>
          </h2>
          <p className="text-gray-600 text-lg max-w-3xl mx-auto leading-relaxed">
            We're an Ontario-based waste management company dedicated to making cleanup easy. 
            Whether you're renovating your home, clearing out a property, or managing a construction 
            site, we deliver the right bin to your door — and haul it away when you're done.
          </p>
        </div>

        {/* Features Grid - Horizontal Cards */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {features.map((feature, index) => (
            <div 
              key={index}
              className="group bg-gray-50 hover:bg-[#1a365d] rounded-xl p-6 transition-all duration-300 hover:shadow-xl"
            >
              <div className="w-14 h-14 bg-blue-100 group-hover:bg-white/20 rounded-xl flex items-center justify-center mb-4 transition-colors">
                <feature.icon className="h-7 w-7 text-[#1a365d] group-hover:text-white transition-colors" />
              </div>
              <h3 className="font-bold text-[#1a365d] group-hover:text-white text-lg mb-2 transition-colors">
                {feature.title}
              </h3>
              <p className="text-gray-500 group-hover:text-white/70 text-sm transition-colors">
                {feature.description}
              </p>
            </div>
          ))}
        </div>

        {/* Image + Content Side by Side */}
        <div className="grid lg:grid-cols-5 gap-8 items-center">
          {/* Images - 3 columns */}
          <div className="lg:col-span-3 grid grid-cols-2 gap-4">
            <div className="space-y-4">
              <div className="rounded-2xl overflow-hidden shadow-lg">
                <img
                  src={binImages.residential}
                  alt="Residential bin rental"
                  className="w-full h-48 object-cover hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="rounded-2xl overflow-hidden shadow-lg">
                <img
                  src={binImages.commercial}
                  alt="Commercial bin rental"
                  className="w-full h-64 object-cover hover:scale-105 transition-transform duration-500"
                />
              </div>
            </div>
            <div className="pt-8">
              <div className="rounded-2xl overflow-hidden shadow-lg">
                <img
                  src={binImages.construction}
                  alt="Construction bin rental"
                  className="w-full h-80 object-cover hover:scale-105 transition-transform duration-500"
                />
              </div>
            </div>
          </div>

          {/* Content - 2 columns */}
          <div className="lg:col-span-2 space-y-6">
            <h3 className="text-3xl font-bold text-[#1a365d]">
              Serving Ontario for Over a Decade
            </h3>
            <p className="text-gray-600 leading-relaxed">
              From the GTA to Hamilton, Niagara, and Barrie — we've built our reputation on 
              reliability and customer service. Our fleet of bins and dedicated team ensure 
              your project stays on track.
            </p>
            <ul className="space-y-3">
              <li className="flex items-center space-x-3">
                <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center">
                  <div className="w-2 h-2 rounded-full bg-green-500"></div>
                </div>
                <span className="text-gray-700">Flexible rental periods (7+ days)</span>
              </li>
              <li className="flex items-center space-x-3">
                <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center">
                  <div className="w-2 h-2 rounded-full bg-green-500"></div>
                </div>
                <span className="text-gray-700">Multiple bin sizes for any project</span>
              </li>
              <li className="flex items-center space-x-3">
                <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center">
                  <div className="w-2 h-2 rounded-full bg-green-500"></div>
                </div>
                <span className="text-gray-700">Responsible recycling practices</span>
              </li>
            </ul>
            <Button
              onClick={() => {
                const element = document.querySelector('#bins');
                if (element) element.scrollIntoView({ behavior: 'smooth' });
              }}
              className="bg-[#1a365d] hover:bg-[#2d4a7c] text-white font-semibold px-8 py-4 text-lg rounded-lg transition-all duration-300 hover:scale-105 mt-4"
            >
              View Our Bins
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
